import { createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Building2, Check, ChevronsUpDown, Plus, User as UserIcon } from "@fleet/icons";
import { cn } from "@fieldcheck/shared";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button, buttonVariants } from "@/components/ui/button";
import { TextField } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { useActiveContext } from "@/features/organizations/hooks/use-active-context";

/**
 * Active-organization switcher (work context). Lists Personal + my
 * orgs and instantly changes the scope of ALL field views (sites/templates/
 * inspections/findings) — the model is already org-centric; the switcher only
 * selects the active `organization_id`. "Add organization" reuses the org
 * bootstrap (onboarding + additional creation) with inline error feedback
 * (CC-30: `openapi-fetch` does not throw on non-2xx). Guide `MULTI_ORG_SWITCHER`.
 */
export function OrgSwitcher(props: { onNavigate?: () => void }) {
  const { t } = useI18n();
  const ctx = useActiveContext();

  const [open, setOpen] = createSignal(false);
  const [creating, setCreating] = createSignal(false);
  const [newName, setNewName] = createSignal("");
  const [submitting, setSubmitting] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const choose = (scopeId: string) => {
    ctx.setActiveOrg(scopeId);
    setOpen(false);
    props.onNavigate?.();
  };

  const submitCreate = async (e: Event) => {
    e.preventDefault();
    const name = newName().trim();
    if (!name || submitting()) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await ctx.bootstrapOrg(name);
      setNewName("");
      setCreating(false);
      setOpen(false);
      props.onNavigate?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : t(msg`Could not create the organization.`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover open={open()} onOpenChange={setOpen}>
      <PopoverTrigger
        as="button"
        type="button"
        class={cn(
          "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
          "border border-border/40 bg-card/40 hover:bg-accent/10",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30",
        )}
        aria-label={t(msg`Switch organization`)}
        data-testid="org-switcher-trigger"
      >
        <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground">
          <Show when={!ctx.activeScope().personal} fallback={<UserIcon class="h-4 w-4" />}>
            <Building2 class="h-4 w-4" />
          </Show>
        </span>
        <span class="min-w-0 flex-1 text-left">
          <span class="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            {t(msg`Organization`)}
          </span>
          <span
            class="block truncate font-medium text-foreground"
            data-testid="org-switcher-active"
          >
            <Show when={!ctx.activeScope().personal} fallback={t(msg`Personal`)}>
              {ctx.activeScope().name}
            </Show>
          </span>
        </span>
        <ChevronsUpDown class="h-4 w-4 shrink-0 opacity-60" />
      </PopoverTrigger>
      <PopoverContent class="w-64 p-1.5" data-testid="org-switcher-menu">
        <p class="px-2 py-1 text-xs font-semibold text-muted-foreground">
          {t(msg`Active context`)}
        </p>
        <div class="max-h-64 overflow-y-auto">
          <For each={ctx.scopes()}>
            {(scope) => {
              const isActive = () => ctx.activeScope().id === scope.id;
              return (
                <button
                  type="button"
                  class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/40 data-[active=true]:bg-accent/30 data-[active=true]:font-semibold"
                  data-active={isActive()}
                  data-testid={scope.personal ? "org-scope-personal" : "org-scope-item"}
                  onClick={() => {
                    choose(scope.id);
                  }}
                >
                  <span class="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
                    <Show when={!scope.personal} fallback={<UserIcon class="h-4 w-4" />}>
                      <Building2 class="h-4 w-4" />
                    </Show>
                  </span>
                  <span class="min-w-0 flex-1 truncate text-left">
                    <Show when={!scope.personal} fallback={t(msg`Personal`)}>
                      {scope.name}
                    </Show>
                  </span>
                  <Show when={isActive()}>
                    <Check class="h-4 w-4 shrink-0 text-primary" />
                  </Show>
                </button>
              );
            }}
          </For>
        </div>

        <div class="my-1 h-px bg-border/50" />

        <Show
          when={creating()}
          fallback={
            <button
              type="button"
              class={cn(
                buttonVariants({ variant: "ghost", size: "sm" }),
                "w-full justify-start gap-2 text-sm",
              )}
              data-testid="org-switcher-add"
              onClick={() => {
                setError(null);
                setCreating(true);
              }}
            >
              <Plus class="h-4 w-4" />
              {t(msg`Add organization`)}
            </button>
          }
        >
          <form class="space-y-2 p-1" onSubmit={(e) => void submitCreate(e)}>
            <TextField
              type="text"
              value={newName()}
              onInput={(e) => setNewName(e.currentTarget.value)}
              placeholder={t(msg`Organization name`)}
              aria-label={t(msg`Organization name`)}
              data-testid="org-switcher-name"
              autofocus
              disabled={submitting()}
            />
            <Show when={error()}>
              <p role="alert" class="text-xs text-destructive" data-testid="org-switcher-error">
                {error()}
              </p>
            </Show>
            <div class="flex gap-2">
              <Button
                type="submit"
                size="sm"
                class="flex-1"
                disabled={submitting() || newName().trim().length === 0}
                data-testid="org-switcher-create"
              >
                <Show when={submitting()} fallback={t(msg`Create`)}>
                  {t(msg`Creating…`)}
                </Show>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={submitting()}
                onClick={() => {
                  setCreating(false);
                  setError(null);
                }}
              >
                {t(msg`Cancel`)}
              </Button>
            </div>
          </form>
        </Show>
      </PopoverContent>
    </Popover>
  );
}
